import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { inputStyle, btnSecondary } from "../components/UI";
import { getDomainPathLabel, getMainDomainId, groupSubdomainsByName, isMainDomain, uniqueSubdomainNames } from "../utils/domainHierarchy";
import { getVolunteerTeamLeadIds } from "../utils/volunteerAssignments";
import {
  formatContributionCurrency,
  getActivityContributionSummary,
  getActivityContributionTotal,
  getActivityContributionValueTotal,
  getTopContributionTypes,
  sumActivitiesContributionTotal,
  sumActivitiesContributionValueTotal,
} from "../utils/activityContributions";

function exportToXlsx(rows, sheetName, fileName) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
}

function fmtDate(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function useMaps(volunteers, domains) {
  const volMap = {};
  volunteers.forEach((v) => { volMap[v.id] = v; });
  const domMap = {};
  domains.forEach((d) => { domMap[d.id] = d; });
  return { volMap, domMap };
}

function firstDayOfMonthIso() {
  const now = new Date();
  return fmtDate(new Date(now.getFullYear(), now.getMonth(), 1));
}

export default function ReportsPage({ volunteers, domains, activities, isMobile = false }) {
  const { volMap, domMap } = useMaps(volunteers, domains);
  const todayIso = fmtDate(new Date());
  const [from, setFrom] = useState(firstDayOfMonthIso());
  const [to, setTo] = useState(todayIso);
  const [mainCategoryId, setMainCategoryId] = useState("all");
  const [subdomainNameFilter, setSubdomainNameFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [teamLeadFilter, setTeamLeadFilter] = useState("all");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const teamLeadOptions = useMemo(
    () => volunteers.filter((volunteer) => volunteer?.status === "active" && volunteer?.isTeamLead === true),
    [volunteers]
  );

  const activeDomains = useMemo(
    () => domains.filter((d) => d.active !== false),
    [domains]
  );

  const domainById = useMemo(() => {
    const m = {};
    activeDomains.forEach((d) => { m[d.id] = d; });
    return m;
  }, [activeDomains]);

  const mainCategories = useMemo(
    () => activeDomains.filter((d) => isMainDomain(d)),
    [activeDomains]
  );

  const childrenByMainId = useMemo(() => {
    const m = {};
    activeDomains.forEach((d) => {
      const parentId = (d.parentDomainId || "").trim();
      if (!parentId) return;
      if (!m[parentId]) m[parentId] = [];
      m[parentId].push(d);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "he")));
    return m;
  }, [activeDomains]);

  const resolveMainCategoryId = (domainId) => getMainDomainId(domainId, domainById);

  const selectedMainChildren = useMemo(() => {
    if (mainCategoryId === "all") return [];
    return childrenByMainId[mainCategoryId] || [];
  }, [mainCategoryId, childrenByMainId]);

  const subdomainNameOptions = useMemo(
    () => uniqueSubdomainNames(selectedMainChildren),
    [selectedMainChildren]
  );

  const cityOptions = useMemo(() => {
    if (mainCategoryId === "all" || subdomainNameFilter === "all") return [];
    const set = new Set();
    activities.forEach((a) => {
      const exactDomain = domainById[a.domainId || ""];
      const mainId = resolveMainCategoryId(a.domainId || "");
      if (mainId !== mainCategoryId) return;
      if ((exactDomain?.name || "").trim() !== subdomainNameFilter) return;
      const city = (a.city || exactDomain?.city || "").trim();
      if (city) set.add(city);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "he"));
  }, [activities, domainById, mainCategoryId, subdomainNameFilter, resolveMainCategoryId]);

  const filtered = useMemo(() => {
    return activities.filter((a) => {
      const date = (a.date || "").trim();
      if (from && (!date || date < from)) return false;
      if (to && (!date || date > to)) return false;
      const mainId = resolveMainCategoryId(a.domainId || "");
      if (mainCategoryId !== "all" && mainId !== mainCategoryId) return false;
      if (subdomainNameFilter !== "all") {
        const exactDomain = domainById[a.domainId || ""];
        const exactName = (exactDomain?.name || "").trim();
        if (exactName !== subdomainNameFilter) return false;
      }
      if (cityFilter !== "all") {
        const city = (a.city || domainById[a.domainId || ""]?.city || "").trim();
        if (city !== cityFilter) return false;
      }
      if (teamLeadFilter !== "all") {
        const volunteer = volMap[(a.volunteerId || "").trim()];
        const volunteerTeamLeadIds = getVolunteerTeamLeadIds(volunteer);
        if ((a.volunteerId || "").trim() !== teamLeadFilter && !volunteerTeamLeadIds.includes(teamLeadFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [activities, from, to, mainCategoryId, subdomainNameFilter, cityFilter, teamLeadFilter, domainById, volMap]);

  const totalHours = filtered.reduce((s, a) => s + Number(a.duration || 0), 0);
  const totalContributions = sumActivitiesContributionTotal(filtered);
  const totalContributionValue = sumActivitiesContributionValueTotal(filtered);
  const uniqueVolunteers = new Set(filtered.map((a) => (a.volunteerId || "").trim()).filter(Boolean)).size;
  const topContributionTypes = getTopContributionTypes(filtered, 4);

  const groupedByMain = useMemo(() => {
    const groups = new Map();
    filtered.forEach((a) => {
      const rawDomain = domainById[a.domainId] || null;
      const mainId = resolveMainCategoryId(a.domainId || "") || "__none";
      const mainDomain = domainById[mainId] || (rawDomain && isMainDomain(rawDomain) ? rawDomain : null);
      const mainName = mainDomain?.name || "ללא קטגוריה";
      const mainColor = mainDomain?.color || "#64748B";
      if (!groups.has(mainId)) {
        groups.set(mainId, {
          id: mainId,
          name: mainName,
          color: mainColor,
          count: 0,
          hours: 0,
          contributions: 0,
          contributionValue: 0,
          volunteers: new Set(),
          subgroups: new Map(),
        });
      }
      const g = groups.get(mainId);
      g.count += 1;
      g.hours += Number(a.duration || 0);
      g.contributions += getActivityContributionTotal(a);
      g.contributionValue += getActivityContributionValueTotal(a);
      if ((a.volunteerId || "").trim()) g.volunteers.add((a.volunteerId || "").trim());

      const exactDomainId = (a.domainId || "").trim() || "__none";
      const exactDomain = domainById[exactDomainId] || null;
      const subGroupName = exactDomain?.name || (mainId === exactDomainId ? "ללא תחום משנה" : "ללא תת־תחום");
      const subGroupKey = `${mainId}__${subGroupName.toLowerCase()}`;
      if (!g.subgroups.has(subGroupKey)) {
        g.subgroups.set(subGroupKey, {
          id: subGroupKey,
          name: subGroupName,
          count: 0,
          hours: 0,
          contributions: 0,
          contributionValue: 0,
          volunteers: new Set(),
          details: new Map(),
          cityGroups: new Map(),
        });
      }
      const sg = g.subgroups.get(subGroupKey);
      sg.count += 1;
      sg.hours += Number(a.duration || 0);
      sg.contributions += getActivityContributionTotal(a);
      sg.contributionValue += getActivityContributionValueTotal(a);
      if ((a.volunteerId || "").trim()) sg.volunteers.add((a.volunteerId || "").trim());

      const cityName = (a.city || exactDomain?.city || "").trim() || "ללא עיר";
      const cityKey = cityName.toLowerCase();
      if (!sg.cityGroups.has(cityKey)) {
        sg.cityGroups.set(cityKey, {
          id: cityKey,
          city: cityName,
          label: cityName,
          count: 0,
          hours: 0,
          contributions: 0,
          contributionValue: 0,
          volunteers: new Set(),
          details: new Map(),
        });
      }
      const cg = sg.cityGroups.get(cityKey);
      cg.count += 1;
      cg.hours += Number(a.duration || 0);
      cg.contributions += getActivityContributionTotal(a);
      cg.contributionValue += getActivityContributionValueTotal(a);
      if ((a.volunteerId || "").trim()) cg.volunteers.add((a.volunteerId || "").trim());

      const freeSub = (a.subdomain || "").trim();
      if (freeSub) {
        sg.details.set(freeSub, (sg.details.get(freeSub) || 0) + 1);
        cg.details.set(freeSub, (cg.details.get(freeSub) || 0) + 1);
      }
    });

    return [...groups.values()]
      .map((g) => ({
        ...g,
        volunteersCount: g.volunteers.size,
        subgroups: [...g.subgroups.values()]
          .map((sg) => ({
            ...sg,
            volunteersCount: sg.volunteers.size,
            details: [...sg.details.entries()]
              .sort((a, b) => b[1] - a[1])
              .slice(0, 4),
            cityGroups: [...sg.cityGroups.values()]
              .map((cg) => ({
                ...cg,
                volunteersCount: cg.volunteers.size,
                details: [...cg.details.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4),
              }))
              .sort((a, b) => b.hours - a.hours || b.count - a.count),
          }))
          .sort((a, b) => b.hours - a.hours || b.count - a.count),
      }))
      .sort((a, b) => b.hours - a.hours || b.count - a.count);
  }, [filtered, domainById]);

  const exportExcel = () => {
    const rows = filtered
      .slice()
      .sort((a, b) => ((b.date || "") + (b.time || "")).localeCompare((a.date || "") + (a.time || "")))
      .map((a) => {
        const exactDomain = domMap[a.domainId];
        const mainId = resolveMainCategoryId(a.domainId || "");
        const mainDomain = domMap[mainId];
        return {
          "תאריך": a.date || "",
          "שעה": a.time || "",
          "קטגוריה ראשית": mainDomain?.name || exactDomain?.name || "",
          "תת תחום": exactDomain?.name || "",
          "עיר": a.city || exactDomain?.city || "",
          "פירוט נוסף": a.subdomain || "",
          "מתנדב": volMap[a.volunteerId]?.name || "",
          "ראש צוות": getVolunteerTeamLeadIds(volMap[a.volunteerId]).map((id) => volMap[id]?.name || id).join(", "),
          "משך": Number(a.duration || 0),
          "תרומות": getActivityContributionSummary(a),
          "סה\"כ פריטים": getActivityContributionTotal(a),
          "שווי תרומות בש\"ח": getActivityContributionValueTotal(a),
          "הערות": a.notes || "",
        };
      });
    exportToXlsx(rows, "Report", `report-${fmtDate(new Date())}.xlsx`);
  };

  const topGroupsChart = groupedByMain.slice(0, 8);
  const maxChartHours = Math.max(...topGroupsChart.map((g) => g.hours), 0);

  const resetFilters = () => {
    setFrom(firstDayOfMonthIso());
    setTo(todayIso);
    setMainCategoryId("all");
    setSubdomainNameFilter("all");
    setCityFilter("all");
    setTeamLeadFilter("all");
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const summaryCard = (label, value, accent, sub = "") => (
    <div style={{
      background: "linear-gradient(180deg, #FFFFFF, #FBFBFD)",
      border: "1px solid #E8E8ED",
      borderTop: `3px solid ${accent}`,
      borderRadius: 14,
      padding: "14px 16px",
      minWidth: isMobile ? "100%" : 170,
      boxShadow: "0 1px 4px rgba(0,0,0,.03)",
    }}>
      <div style={{ fontSize: 12, color: "#8E8EA0", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#1A1A2E", fontFamily: "Assistant" }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: "#A3A3B5", marginTop: 3 }}>{sub}</div> : null}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl" }}>
    <div style={{
      background: "linear-gradient(180deg, #FFFFFF, #FCFCFE)",
      border: "1px solid #E5E7EB",
      borderRadius: 16,
      padding: 16,
      marginBottom: 14,
      boxShadow: "0 10px 24px rgba(15,23,42,.04)",
    }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#1A1A2E" }}>דוחות פעילות</div>
            <div style={{ fontSize: 13, color: "#8E8EA0" }}>סיכום לפי קטגוריות ראשיות ותתי־תחומים</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={{ ...btnSecondary, borderRadius: 12, padding: "9px 14px" }} onClick={resetFilters}>איפוס סינון</button>
            <button style={{ ...btnSecondary, borderRadius: 12, padding: "9px 14px", background: "#EDF4FF", borderColor: "#BFDBFE", color: "#1D4ED8" }} onClick={exportExcel}>ייצוא אקסל</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(6, minmax(0,1fr))", gap: 10, marginBottom: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#737373", marginBottom: 4 }}>מתאריך</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: "100%", borderRadius: 12 }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#737373", marginBottom: 4 }}>עד תאריך</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: "100%", borderRadius: 12 }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#737373", marginBottom: 4 }}>קטגוריה ראשית</label>
            <select
              value={mainCategoryId}
              onChange={(e) => {
                setMainCategoryId(e.target.value);
                setSubdomainNameFilter("all");
                setCityFilter("all");
              }}
              style={{ ...inputStyle, width: "100%", borderRadius: 12, cursor: "pointer" }}
            >
              <option value="all">כל הקטגוריות</option>
              {mainCategories.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#737373", marginBottom: 4 }}>תת־תחום (שם)</label>
            <select
              value={subdomainNameFilter}
              onChange={(e) => {
                setSubdomainNameFilter(e.target.value);
                setCityFilter("all");
              }}
              style={{ ...inputStyle, width: "100%", borderRadius: 12, cursor: "pointer" }}
              disabled={mainCategoryId === "all"}
            >
              <option value="all">{mainCategoryId === "all" ? "בחר קטגוריה ראשית קודם" : "כל תתי התחום"}</option>
              {subdomainNameOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#737373", marginBottom: 4 }}>עיר</label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              style={{ ...inputStyle, width: "100%", borderRadius: 12, cursor: "pointer" }}
              disabled={mainCategoryId === "all" || subdomainNameFilter === "all"}
            >
              <option value="all">{subdomainNameFilter === "all" ? "בחר תת־תחום קודם" : "כל הערים"}</option>
              {cityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#737373", marginBottom: 4 }}>ראש צוות</label>
            <select
              value={teamLeadFilter}
              onChange={(e) => setTeamLeadFilter(e.target.value)}
              style={{ ...inputStyle, width: "100%", borderRadius: 12, cursor: "pointer" }}
            >
              <option value="all">כל ראשי הצוות</option>
              {teamLeadOptions.map((teamLead) => <option key={teamLead.id} value={teamLead.id}>{teamLead.name || "ללא שם"}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {summaryCard("סה\"כ פעילויות", filtered.length, "#2563EB")}
          {summaryCard("סה\"כ שעות", totalHours.toFixed(1), "#16A34A")}
          {summaryCard("סה\"כ תרומות", totalContributions, "#C2410C", topContributionTypes.length ? topContributionTypes.map((item) => `${item.type} (${item.quantity})`).join(" · ") : "")}
          {summaryCard("שווי תרומות", formatContributionCurrency(totalContributionValue) || "—", "#0F766E")}
          {summaryCard("מתנדבים שונים", uniqueVolunteers, "#C41E3A")}
          {summaryCard("קטגוריות פעילות", groupedByMain.length, "#D97706", groupedByMain.length ? "בטווח הנבחר" : "")}
        </div>

        {topGroupsChart.length > 0 && (
          <div style={{
            marginTop: 14,
            border: "1px solid #E8E8ED",
            borderRadius: 14,
            padding: 12,
            background: "linear-gradient(180deg, #FFFFFF, #FBFCFF)",
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1A1A2E", marginBottom: 10 }}>
              התפלגות שעות לפי קטגוריה ראשית
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {topGroupsChart.map((g) => {
                const pct = maxChartHours > 0 ? (g.hours / maxChartHours) * 100 : 0;
                return (
                  <div key={`chart-${g.id}`} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "190px 1fr 76px", gap: 8, alignItems: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {g.name}
                    </div>
                    <div style={{ height: 10, borderRadius: 999, background: "#EEF2F7", overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(pct, 2)}%`, height: "100%", background: `linear-gradient(90deg, ${g.color}, ${g.color}CC)` }} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", textAlign: isMobile ? "right" : "left" }}>
                      {g.hours.toFixed(1)} ש׳
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {groupedByMain.map((group) => (
          <div
            key={group.id}
            style={{
              background: "#fff",
              border: "1px solid #E6EAF0",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 8px 18px rgba(15,23,42,.03)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "12px 14px",
              borderBottom: "1px solid #F1F1F5",
              background: `linear-gradient(180deg, ${group.color}14, #FFFFFF)`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  style={{
                    border: "1px solid #E5E7EB",
                    background: "#fff",
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    cursor: "pointer",
                    color: "#4B5563",
                    fontWeight: 800,
                    lineHeight: 1,
                    fontFamily: "inherit",
                  }}
                  title={collapsedGroups[group.id] ? "פתח" : "סגור"}
                >
                  {collapsedGroups[group.id] ? "+" : "−"}
                </button>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: group.color, boxShadow: `0 0 0 4px ${group.color}20` }} />
                <div style={{ fontWeight: 800, color: "#1A1A2E" }}>{group.name}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 999, padding: "4px 10px" }}>
                  {group.count} פעילויות
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#166534", background: "#ECFDF5", border: "1px solid #BBF7D0", borderRadius: 999, padding: "4px 10px" }}>
                  {group.hours.toFixed(1)} שעות
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#9A3412", background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 999, padding: "4px 10px" }}>
                  {group.contributions} תרומות
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#0F766E", background: "#ECFEFF", border: "1px solid #A5F3FC", borderRadius: 999, padding: "4px 10px" }}>
                  {formatContributionCurrency(group.contributionValue) || "—"}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#7C2D12", background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 999, padding: "4px 10px" }}>
                  {group.volunteersCount} מתנדבים
                </span>
              </div>
            </div>

            {!collapsedGroups[group.id] && (
            <div style={{ padding: 10 }}>
              {!isMobile ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["תת־תחום", "עיר", "כמות פעילויות", "%", "סה\"כ שעות", "תרומות", "שווי", "מתנדבים", "פירוט נוסף נפוץ"].map((h) => (
                        <th key={h} style={{ textAlign: "right", fontSize: 11, color: "#8E8EA0", padding: "8px 10px", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.subgroups.map((sg) => (
                      <React.Fragment key={sg.id}>
                        <tr>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", fontWeight: 800, color: "#2D2D3F", background: "#FCFCFE" }}>
                            {sg.name}
                          </td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE", color: "#6B7280" }}>
                            {sg.cityGroups.length} ערים
                          </td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE" }}>{sg.count}</td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 72, height: 7, borderRadius: 999, background: "#EEF2F7", overflow: "hidden" }}>
                                <div style={{ width: `${group.count ? (sg.count / group.count) * 100 : 0}%`, height: "100%", background: group.color }} />
                              </div>
                              <span style={{ fontSize: 12, color: "#6B7280" }}>
                                {group.count ? `${Math.round((sg.count / group.count) * 100)}%` : "0%"}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE" }}>{sg.hours.toFixed(1)}</td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE" }}>{sg.contributions}</td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE", color: "#0F766E", fontWeight: 700 }}>{formatContributionCurrency(sg.contributionValue) || "—"}</td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE" }}>{sg.volunteersCount}</td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #EEF2F7", background: "#FCFCFE", color: "#6B7280", fontSize: 12 }}>
                            {sg.details.length ? sg.details.map(([name, count]) => `${name} (${count})`).join(" · ") : "—"}
                          </td>
                        </tr>
                        {sg.cityGroups.map((cg) => (
                          <tr key={`${sg.id}-${cg.id}`}>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB", color: "#6B7280", fontSize: 12 }}>↳ {sg.name}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB", fontWeight: 700, color: "#1D4ED8" }}>{cg.city}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB" }}>{cg.count}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 72, height: 7, borderRadius: 999, background: "#EEF2F7", overflow: "hidden" }}>
                                  <div style={{ width: `${sg.count ? (cg.count / sg.count) * 100 : 0}%`, height: "100%", background: "#64748B" }} />
                                </div>
                                <span style={{ fontSize: 12, color: "#6B7280" }}>
                                  {sg.count ? `${Math.round((cg.count / sg.count) * 100)}%` : "0%"}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB" }}>{cg.hours.toFixed(1)}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB" }}>{cg.contributions}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB", color: "#0F766E", fontWeight: 700 }}>{formatContributionCurrency(cg.contributionValue) || "—"}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB" }}>{cg.volunteersCount}</td>
                            <td style={{ padding: "10px", borderBottom: "1px solid #F8F8FB", color: "#6B7280", fontSize: 12 }}>
                              {cg.details.length ? cg.details.map(([name, count]) => `${name} (${count})`).join(" · ") : "—"}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {group.subgroups.map((sg) => (
                    <div key={sg.id} style={{ border: "1px solid #F0F0F5", borderRadius: 12, padding: 10, background: "#FCFCFE" }}>
                      <div style={{ fontWeight: 700, color: "#2D2D3F", marginBottom: 6 }}>{sg.name}</div>
                      <div style={{ marginBottom: 6 }}>
                        <div style={{ width: "100%", height: 8, borderRadius: 999, background: "#EEF2F7", overflow: "hidden" }}>
                          <div style={{ width: `${group.count ? (sg.count / group.count) * 100 : 0}%`, height: "100%", background: group.color }} />
                        </div>
                        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                          {group.count ? `${Math.round((sg.count / group.count) * 100)}% מהקטגוריה` : "0%"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, background: "#F5F5F7", borderRadius: 999, padding: "3px 8px" }}>{sg.count} פעילויות</span>
                        <span style={{ fontSize: 12, background: "#ECFDF5", color: "#166534", borderRadius: 999, padding: "3px 8px" }}>{sg.hours.toFixed(1)} ש׳</span>
                        <span style={{ fontSize: 12, background: "#FFF7ED", color: "#9A3412", borderRadius: 999, padding: "3px 8px" }}>{sg.contributions} תרומות</span>
                        <span style={{ fontSize: 12, background: "#ECFEFF", color: "#0F766E", borderRadius: 999, padding: "3px 8px" }}>{formatContributionCurrency(sg.contributionValue) || "—"}</span>
                        <span style={{ fontSize: 12, background: "#FFF7ED", color: "#9A3412", borderRadius: 999, padding: "3px 8px" }}>{sg.volunteersCount} מתנדבים</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>
                        {sg.details.length ? sg.details.map(([name, count]) => `${name} (${count})`).join(" · ") : "ללא פירוט נוסף"}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {sg.cityGroups.map((cg) => (
                          <div key={`${sg.id}-${cg.id}`} style={{ border: "1px solid #EEF2F7", borderRadius: 10, padding: 8, background: "white" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#1D4ED8" }}>{cg.city}</div>
                              <div style={{ fontSize: 11, color: "#6B7280" }}>{cg.count} פע׳ · {cg.hours.toFixed(1)} ש׳</div>
                            </div>
                            <div style={{ fontSize: 11, color: "#6B7280" }}>
                              {cg.details.length ? cg.details.map(([name, count]) => `${name} (${count})`).join(" · ") : "ללא פירוט נוסף"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
        ))}

        {groupedByMain.length === 0 && (
          <div style={{
            background: "#fff",
            border: "1px solid #E8E8ED",
            borderRadius: 16,
            padding: "36px 20px",
            textAlign: "center",
            color: "#A3A3B5",
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#525264", marginBottom: 4 }}>אין נתונים לתצוגה</div>
            <div style={{ fontSize: 13 }}>שנה טווח תאריכים או סינון קטגוריות</div>
          </div>
        )}
      </div>

      <div style={{
        marginTop: 14,
        background: "#fff",
        border: "1px solid #E8E8ED",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,.03)",
      }}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid #F1F1F5", background: "#FAFAFC", fontWeight: 800, color: "#1A1A2E" }}>
          פירוט פעילויות (עד 300 רשומות)
        </div>
        {!isMobile ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 950 }}>
              <thead>
                <tr>
                  {["תאריך", "שעה", "קטגוריה ראשית", "תת תחום", "פירוט נוסף", "מתנדב", "משך", "תרומות", "שווי", "הערות"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#8E8EA0", textAlign: "right", background: "#FCFCFE", borderBottom: "1px solid #EEF0F3" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 300).map((a) => {
                  const exactDomain = domMap[a.domainId];
                  const mainDomain = domMap[resolveMainCategoryId(a.domainId || "")];
                  return (
                    <tr key={a.id}>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{a.date || ""}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{a.time || ""}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{mainDomain?.name || exactDomain?.name || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>
                        <div>{exactDomain?.name || "—"}</div>
                        {!!(a.city || exactDomain?.city || "").trim() && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>עיר: {a.city || exactDomain?.city}</div>}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{a.subdomain || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{volMap[a.volunteerId]?.name || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{Number(a.duration || 0)}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA", color: "#9A3412" }}>{getActivityContributionSummary(a) || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA", color: "#0F766E", fontWeight: 700 }}>{formatContributionCurrency(getActivityContributionValueTotal(a)) || "—"}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #F7F7FA" }}>{a.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.slice(0, 300).map((a) => {
              const exactDomain = domMap[a.domainId];
              const mainDomain = domMap[resolveMainCategoryId(a.domainId || "")];
              return (
                <div key={a.id} style={{ border: "1px solid #F0F0F5", borderRadius: 12, padding: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontWeight: 700 }}>{volMap[a.volunteerId]?.name || "—"}</div>
                    <div style={{ fontSize: 12, color: "#737373" }}>{a.date || ""} {a.time || ""}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#525264", marginTop: 4 }}>
                    {mainDomain?.name || exactDomain?.name || "—"} / {exactDomain?.name || "—"}
                  </div>
                  {!!(a.city || exactDomain?.city || "").trim() && (
                    <div style={{ fontSize: 12, color: "#6B7280", marginTop: 3 }}>עיר: {a.city || exactDomain?.city}</div>
                  )}
                  {!!(a.subdomain || "").trim() && (
                    <div style={{ fontSize: 12, color: "#8E8EA0", marginTop: 3 }}>פירוט: {a.subdomain}</div>
                  )}
                  <div style={{ fontSize: 12, color: "#737373", marginTop: 3 }}>{Number(a.duration || 0)} שעות</div>
                  {getActivityContributionSummary(a) && <div style={{ fontSize: 12, color: "#9A3412", marginTop: 3 }}>תרומות: {getActivityContributionSummary(a)}</div>}
                  {getActivityContributionValueTotal(a) > 0 && <div style={{ fontSize: 12, color: "#0F766E", marginTop: 3, fontWeight: 700 }}>שווי: {formatContributionCurrency(getActivityContributionValueTotal(a))}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
